import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AutoPostingStatus } from './AutoPostingToggle';
import { DateRangeField } from './DateRangeField';
import { getErrorMessage } from '../api/client';
import { applyInventoryReturns, createRecipe, getIngredients, getInventoryUnits, getPresentations, getInventoryReturnsPreview, getSalesConsumptionPreview, getSalesConsumptionReadiness, linkRecipeLoyverse, postSalesConsumption } from '../api/inventory';
import { getStores } from '../api/stores';
import { useAuth } from '../auth/AuthProvider';
import { useLoyversePresence } from '../utils/loyversePresence';
import { getStoreScopeOptions } from '../auth/permissions';
import { Modal } from './Modal';
import { ManualMovementModal } from './InventoryStockPanel';
import { PresentationEditor } from './PresentationEditor';
import { SalesPostingHistoryPanel } from './SalesPostingHistoryPanel';
import { DEFAULT_STORE_TIMEZONE, dateToStoreISO, resolveStoreTimezone } from '../utils/timezones';
import type {
  ConsumptionIssueType,
  ConsumptionReadiness,
  Ingredient,
  InventoryReturnLine,
  InventoryReturnsPreview,
  InventoryUnit,
  PurchasePresentation,
  RealCostStatus,
  SalesConsumptionPostResult,
  SalesConsumptionPreview,
  SalesConsumptionPreviewFilters,
  SalesConsumptionSortKey,
  StockPreviewStatus,
  SalesConsumptionIssue,
} from '../types/inventory';
import type { Store } from '../types/stores';
import { formatOptionalMoney, formatOptionalSignedMoney, formatQuantity } from '../utils/costFormatting';
import { nextTableSort, sortChoices, type SortWording, type TableSort } from '../utils/tableSort';

const today = new Date();
const sevenDaysAgo = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1_000);
const CONFIRM_WORD = 'DESCONTAR';
/**
 * Página de la tabla de insumos y del detalle por ticket. Antes pedía 100 de golpe y NO había forma
 * de ver el resto: un rango con 300 insumos afectados mostraba 100 y el título decía 300.
 */
const PAGE_SIZE = 50;
const DEFAULT_SORT: TableSort<SalesConsumptionSortKey> = { key: 'ingredient', dir: 'asc' };
/** Cómo se lee cada columna al ordenar. Alimenta el selector de móvil y la etiqueta del orden actual. */
const SORT_WORDING: SortWording<SalesConsumptionSortKey> = {
  ingredient: ['Insumo', 'A–Z', 'Z–A'],
  store: ['Tienda', 'A–Z', 'Z–A'],
  // «Sin costo» va siempre al final, suba o baje el orden (lo decide el servidor).
  plannedCost: ['Costo estimado', 'menor primero', 'mayor primero'],
  realCost: ['Costo real promedio', 'menor primero', 'mayor primero'],
  difference: ['Diferencia', 'menor primero', 'mayor primero'],
  // Los estados ordenan por gravedad (bien → falta stock → unidad incompatible), no por su nombre.
  stockStatus: ['Estado inventario', 'primero lo que está bien', 'primero lo que falla'],
  realCostStatus: ['Estado costo real', 'primero con costo', 'primero sin costo'],
};
/** Los órdenes que se ofrecen de un toque en el teléfono. */
const SORT_CHOICES = [
  ['ingredient', 'asc'], ['difference', 'desc'], ['plannedCost', 'desc'], ['realCost', 'desc'],
  ['stockStatus', 'desc'], ['realCostStatus', 'desc'], ['store', 'asc'],
] as const;
/** Desplazamiento de cada tabla paginada. Van separados: avanzar el detalle por ticket no mueve la de insumos. */
interface PageState { offset: number; receiptOffset: number }
const PRIMERA_PAGINA: PageState = { offset: 0, receiptOffset: 0 };
// Ciclo cerrado (Ola 1): el rango/tienda elegidos sobreviven el viaje a una pantalla de arreglo
// (receta, compra) y la entrada de un toque desde Inicio. Solo conveniencia de UX en este
// dispositivo; el backend valida todo como siempre.
const FILTERS_KEY = 'n360.descuento.filtros';
const ENTRY_TODAY_KEY = 'n360.descuento.entrada-hoy';
/** Día de la venta pendiente más vieja, que el Inicio deja puesto al entrar de un toque. */
const ENTRY_FROM_KEY = 'n360.descuento.desde';
const RETURN_KEY = 'n360.descuento.volver';

type FixModal =
  | { kind: 'presentation'; ingredientId: string; presentation?: PurchasePresentation }
  | { kind: 'adjust'; ingredientId: string; storeId: string | null; focusCost: boolean };

function readStoredFilters(): Partial<SalesConsumptionPreviewFilters> | null {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    return raw ? (JSON.parse(raw) as Partial<SalesConsumptionPreviewFilters>) : null;
  } catch { return null; }
}

export function SalesConsumptionPreviewPanel() {
  const { user } = useAuth();
  const loyverseConnected = useLoyversePresence();
  const [stores, setStores] = useState<Store[]>([]);
  const entryToday = useRef(sessionStorage.getItem(ENTRY_TODAY_KEY) === '1');
  const entryFrom = useRef(sessionStorage.getItem(ENTRY_FROM_KEY));
  const returning = useRef(sessionStorage.getItem(RETURN_KEY) === '1');
  const [filters, setFilters] = useState<SalesConsumptionPreviewFilters>(() => {
    const base: SalesConsumptionPreviewFilters = {
      dateFrom: toDateInput(sevenDaysAgo),
      dateTo: toDateInput(today),
      storeId: '',
      productSearch: '',
      onlyIssues: false,
      includeReceiptDetails: false,
    };
    // Inicializador PURO (StrictMode monta doble en dev): aquí solo se LEE; las banderas se
    // consumen una única vez en el efecto de auto-cálculo.
    if (entryToday.current) {
      const hoy = toDateInput(new Date());
      // El Inicio manda DESDE CUÁNDO hay ventas sin descontar. Antes se entraba siempre con "hoy",
      // así que si venías arrastrando días, la pantalla mostraba cero y parecía no haber trabajo.
      const desde = entryFrom.current;
      return { ...base, dateFrom: desde && desde < hoy ? desde : hoy, dateTo: hoy };
    }
    if (returning.current) {
      const stored = readStoredFilters();
      if (stored) return { ...base, ...stored };
    }
    return base;
  });
  const [preview, setPreview] = useState<SalesConsumptionPreview>();
  /**
   * Lo que de verdad se calculó la última vez (los filtros del formulario son un borrador hasta que
   * se pulsa «Calcular»). Paginar y ordenar parten de aquí: si no, cambiar una fecha y pulsar
   * «Siguiente» aplicaría a escondidas un filtro que nunca se confirmó.
   */
  const [applied, setApplied] = useState<{ value: SalesConsumptionPreviewFilters; page: PageState }>();
  const [isPaging, setIsPaging] = useState(false);
  // Regresos al inventario: lo que VUELVE cuando una venta se deshace. Vive junto al descuento a
  // propósito — es la otra mitad de la misma pregunta ("¿el inventario refleja lo que se vendió?").
  const [returns, setReturns] = useState<InventoryReturnsPreview>();
  const [isReturning, setIsReturning] = useState(false);
  const [returnsNotice, setReturnsNotice] = useState<string>();
  const [returnsModalOpen, setReturnsModalOpen] = useState(false);
  const [readiness, setReadiness] = useState<ConsumptionReadiness>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [isPostingModalOpen, setIsPostingModalOpen] = useState(false);
  const [postingResult, setPostingResult] = useState<SalesConsumptionPostResult>();
  // "Crear receta" desde un problema Sin receta: crea la receta ya vinculada al producto Loyverse
  // y navega a su detalle con retorno garantizado a esta pantalla.
  const [creatingRecipeKey, setCreatingRecipeKey] = useState<string | null>(null);
  const [createdRecipeKeys, setCreatedRecipeKeys] = useState<Set<string>>(new Set());
  // Arreglos en contexto: modal encima de esta pantalla, con catálogo cargado bajo demanda.
  const [fixModal, setFixModal] = useState<FixModal | null>(null);
  const [fixBusy, setFixBusy] = useState<string | null>(null);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [units, setUnits] = useState<InventoryUnit[]>([]);
  const catalogLoaded = useRef(false);

  const issueProductKey = (issue: SalesConsumptionIssue) => `${issue.loyverseItemId ?? ''}:${issue.loyverseVariantId ?? ''}`;

  const persistFilters = (value: SalesConsumptionPreviewFilters) => {
    try { sessionStorage.setItem(FILTERS_KEY, JSON.stringify({ dateFrom: value.dateFrom, dateTo: value.dateTo, storeId: value.storeId, productSearch: value.productSearch, onlyIssues: value.onlyIssues, includeReceiptDetails: value.includeReceiptDetails, sort: value.sort, dir: value.dir })); } catch { /* almacenamiento no disponible: sin persistencia, sin romper */ }
  };

  const markReturnTrip = () => { try { sessionStorage.setItem(RETURN_KEY, '1'); persistFilters(filters); } catch { /* idem */ } };

  const goWithReturn = (hash: string) => { markReturnTrip(); window.location.hash = hash; };

  const ensureCatalog = async () => {
    if (catalogLoaded.current) return;
    const [ingredientsResult, unitsResult] = await Promise.all([getIngredients(), getInventoryUnits()]);
    setIngredients(ingredientsResult.ingredients);
    setUnits(unitsResult.units);
    catalogLoaded.current = true;
  };

  const createRecipeForIssue = async (issue: SalesConsumptionIssue) => {
    if (!issue.loyverseItemId && !issue.loyverseVariantId) return;
    const productKey = issueProductKey(issue);
    setCreatingRecipeKey(productKey);
    setError(undefined);
    try {
      const name = issue.variantName && issue.variantName !== issue.productName ? `${issue.productName ?? ''} ${issue.variantName}`.trim() : (issue.productName ?? 'Receta nueva');
      const { recipe } = await createRecipe({ name, description: null, outputQuantity: 1, outputUnitId: null });
      await linkRecipeLoyverse(recipe.id, { ...(issue.loyverseItemId ? { itemId: issue.loyverseItemId } : {}), ...(issue.loyverseVariantId ? { variantId: issue.loyverseVariantId } : {}) });
      setCreatedRecipeKeys((prev) => new Set(prev).add(productKey));
      goWithReturn(`recipes/${recipe.id}/volver-descuento`);
    } catch (createError) {
      setError(getErrorMessage(createError, 'No fue posible crear la receta.'));
    } finally {
      setCreatingRecipeKey(null);
    }
  };

  const openPresentationFix = async (issue: SalesConsumptionIssue, editExisting: boolean) => {
    if (!issue.ingredientId) return;
    setFixBusy(`presentation-${issue.ingredientId}`);
    setError(undefined);
    try {
      await ensureCatalog();
      let presentation: PurchasePresentation | undefined;
      if (editExisting) {
        const result = await getPresentations(issue.ingredientId);
        presentation = result.presentations.find((item) => item.isCostingDefault && item.isActive) ?? result.presentations.find((item) => item.isActive) ?? result.presentations[0];
      }
      setFixModal({ kind: 'presentation', ingredientId: issue.ingredientId, presentation });
    } catch (openError) {
      setError(getErrorMessage(openError, 'No fue posible abrir el precio de compra.'));
    } finally {
      setFixBusy(null);
    }
  };

  const openAdjustFix = async (issue: SalesConsumptionIssue, focusCost: boolean) => {
    if (!issue.ingredientId) return;
    setFixBusy(`adjust-${issue.ingredientId}`);
    setError(undefined);
    try {
      await ensureCatalog();
      setFixModal({ kind: 'adjust', ingredientId: issue.ingredientId, storeId: issue.storeId ?? (filters.storeId || null), focusCost });
    } catch (openError) {
      setError(getErrorMessage(openError, 'No fue posible abrir el ajuste de inventario.'));
    } finally {
      setFixBusy(null);
    }
  };

  useEffect(() => {
    void getStores().then((result) => {
      setStores(result.stores);
      const { defaultStoreId } = getStoreScopeOptions(user?.role, result.stores);
      // No pisar la tienda restaurada de un viaje de arreglo o de la entrada desde Inicio.
      if (defaultStoreId) setFilters((current) => (current.storeId ? current : { ...current, storeId: defaultStoreId }));
    }).catch((loadError) => setError(getErrorMessage(loadError, 'No fue posible cargar las tiendas.')));
  }, [user?.role]);

  /**
   * `conservarResultado` existe por el recálculo automático que corre JUSTO DESPUÉS de descontar:
   * sin él borraba el resultado recién obtenido —incluida la lista de qué se quedó fuera— y el
   * dueño no alcanzaba a leer nada. Un recálculo pedido a mano sí lo limpia: ya es otra consulta.
   */
  const runCalculation = async (value: SalesConsumptionPreviewFilters, conservarResultado = false) => {
    if (isLoading) return;
    setIsLoading(true);
    setError(undefined);
    if (!conservarResultado) setPostingResult(undefined);
    try {
      const request = buildRequest(value, PRIMERA_PAGINA);
      const [previewResult, readinessResult, returnsResult] = await Promise.all([
        getSalesConsumptionPreview(request),
        getSalesConsumptionReadiness({ dateFrom: request.dateFrom, dateTo: request.dateTo, storeId: request.storeId }),
        // Si esto falla no se cae la simulación entera: los regresos son un extra, no el cálculo.
        getInventoryReturnsPreview(request).catch(() => undefined),
      ]);
      setPreview(previewResult);
      setReadiness(readinessResult);
      setReturns(returnsResult);
      setApplied({ value, page: PRIMERA_PAGINA });
      persistFilters(value);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'No fue posible calcular la vista previa.'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cambiar de página u orden trae SOLO la tabla: readiness y regresos no dependen de la página. Y no
   * apaga la pantalla como el cálculo completo: mientras llega, las tablas se atenúan y los botones
   * se deshabilitan.
   */
  const loadPage = async (value: SalesConsumptionPreviewFilters, page: PageState) => {
    if (isLoading || isPaging) return;
    setIsPaging(true);
    setError(undefined);
    try {
      setPreview(await getSalesConsumptionPreview(buildRequest(value, page)));
      setApplied({ value, page });
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'No fue posible cambiar de página.'));
    } finally {
      setIsPaging(false);
    }
  };

  const currentSort: TableSort<SalesConsumptionSortKey> = { key: applied?.value.sort ?? DEFAULT_SORT.key, dir: applied?.value.dir ?? DEFAULT_SORT.dir };
  const sortBy = (next: TableSort<SalesConsumptionSortKey>) => {
    if (!applied) return;
    // También al borrador, para que el siguiente «Calcular» conserve el orden elegido.
    setFilters((current) => ({ ...current, sort: next.key, dir: next.dir }));
    // Reordenar vuelve a la primera página de insumos; el detalle por ticket no cambia de orden y
    // se queda donde estaba.
    void loadPage({ ...applied.value, sort: next.key, dir: next.dir }, { offset: 0, receiptOffset: applied.page.receiptOffset });
  };
  const th = (key: SalesConsumptionSortKey, label: string) => {
    const active = currentSort.key === key;
    return <th aria-sort={active ? (currentSort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button className="data-table__sort" disabled={isPaging} onClick={() => sortBy(nextTableSort(currentSort, key))} type="button">{label}
        <span aria-hidden="true" className={active ? 'data-table__sort-arrow data-table__sort-arrow--active' : 'data-table__sort-arrow'}>{active ? (currentSort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>;
  };
  const tableWrapClass = isPaging ? 'data-table-wrap data-table-wrap--paging' : 'data-table-wrap';

  /**
   * Regresa al inventario lo que volvió a la tienda. Pide confirmación con motivo por la misma razón
   * que la reversa de un descuento: un movimiento que SUBE existencias sin explicación es
   * indistinguible de un error de captura cuando alguien lo audite meses después.
   */
  const applyReturns = async (motivo: string) => {
    if (isReturning) return;
    setIsReturning(true);
    setError(undefined);
    try {
      const result = await applyInventoryReturns({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        storeId: filters.storeId || undefined,
        confirm: true,
        reason: motivo.trim(),
      });
      setReturnsModalOpen(false);
      setReturnsNotice(result.message);
      await runCalculation(filters);
    } catch (returnError) {
      setError(getErrorMessage(returnError, 'No fue posible regresar el producto al inventario.'));
    } finally {
      setIsReturning(false);
    }
  };

  // Entrada de un toque desde Inicio o regreso de una pantalla de arreglo: calcular solo, sin
  // pedir un clic extra. Una sola vez al montar; sin bucles de re-fetch.
  const autoRan = useRef(false);
  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;
    if (entryToday.current || returning.current) {
      try { sessionStorage.removeItem(ENTRY_TODAY_KEY); sessionStorage.removeItem(ENTRY_FROM_KEY); sessionStorage.removeItem(RETURN_KEY); } catch { /* sin almacenamiento */ }
      void runCalculation(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculate = async (event: FormEvent) => {
    event.preventDefault();
    await runCalculation(filters);
  };

  const afterFixSaved = async () => {
    setFixModal(null);
    await runCalculation(filters);
  };

  // El backend dice cuál bloquea (`blocking`); la pantalla no lo deduce de la severidad.
  const blockingIssues = (preview?.issueItems ?? []).filter((issue) => issue.blocking);
  const infoIssues = (preview?.issueItems ?? []).filter((issue) => !issue.blocking);
  const storeName = stores.find((store) => store.id === filters.storeId)?.name ?? null;
  const pendingCount = readiness?.pendingSaleLinesCount;
  const postedCount = readiness?.postedSaleLinesCount ?? 0;
  const totalEligible = readiness?.totalEligibleSaleLinesCount ?? 0;
  const alreadyApplied = pendingCount === 0 && totalEligible > 0;

  const fixAction = (issue: SalesConsumptionIssue) => {
    if (issue.issueType === 'MISSING_RECIPE' && (issue.loyverseItemId || issue.loyverseVariantId)) {
      return <button className="text-button" disabled={creatingRecipeKey !== null} onClick={() => void createRecipeForIssue(issue)} type="button">{createdRecipeKeys.has(issueProductKey(issue)) ? 'Receta creada ✓' : creatingRecipeKey === issueProductKey(issue) ? 'Creando…' : 'Crear receta'}</button>;
    }
    if (issue.issueType === 'MISSING_PURCHASE_PRICE' && issue.ingredientId) {
      return <button className="text-button" disabled={fixBusy !== null} onClick={() => void openPresentationFix(issue, false)} type="button">{fixBusy === `presentation-${issue.ingredientId}` ? 'Abriendo…' : 'Capturar precio de compra'}</button>;
    }
    if (issue.issueType === 'INCOMPLETE_RECIPE' && issue.ingredientId) {
      return <button className="text-button" disabled={fixBusy !== null} onClick={() => void openPresentationFix(issue, false)} type="button">{fixBusy === `presentation-${issue.ingredientId}` ? 'Abriendo…' : 'Capturar precio de compra'}</button>;
    }
    if (issue.issueType === 'INCOMPLETE_RECIPE' && issue.recipeId) {
      return <button className="text-button" onClick={() => goWithReturn(`recipes/${issue.recipeId}/volver-descuento`)} type="button">Abrir receta</button>;
    }
    if ((issue.issueType === 'INSUFFICIENT_STOCK' || issue.issueType === 'NO_STOCK_RECORD') && issue.ingredientId) {
      return <div className="issue-action-group">
        <button className="text-button" disabled={fixBusy !== null} onClick={() => void openAdjustFix(issue, false)} type="button">{fixBusy === `adjust-${issue.ingredientId}` ? 'Abriendo…' : 'Ajustar inventario'}</button>
        <button className="text-button" onClick={() => goWithReturn('purchases/volver-descuento')} type="button">Registrar compra</button>
      </div>;
    }
    if (issue.issueType === 'MISSING_REAL_COST' && issue.ingredientId) {
      return <button className="text-button" disabled={fixBusy !== null} onClick={() => void openAdjustFix(issue, true)} type="button">{fixBusy === `adjust-${issue.ingredientId}` ? 'Abriendo…' : 'Ajustar con costo'}</button>;
    }
    if (issue.issueType === 'COST_DIFFERENCE_HIGH' && issue.ingredientId) {
      return <button className="text-button" disabled={fixBusy !== null} onClick={() => void openPresentationFix(issue, true)} type="button">{fixBusy === `presentation-${issue.ingredientId}` ? 'Abriendo…' : 'Revisar precio'}</button>;
    }
    if (issue.issueType === 'UNIT_MISMATCH') {
      return <button className="text-button" onClick={() => goWithReturn('recipes/volver-descuento')} type="button">Revisar recetas</button>;
    }
    return <span>{issue.suggestedAction}</span>;
  };

  // ZTV-03/ZTV-04: el descuento por ventas procesa tickets Loyverse; sin la integración conectada
  // se declara el alcance (las Ventas Otros con receta ya descuentan solas al confirmarse).
  if (loyverseConnected === false) {
    return <div className="consumption-preview-panel"><div className="empty-panel">
      <strong>Esta herramienta descuenta inventario de las ventas conectadas desde Loyverse</strong>
      <span>Tu negocio no tiene esa integración conectada — y no la necesita para descontar inventario: las ventas registradas aquí con receta asignada descuentan solas al confirmarse.</span>
    </div></div>;
  }

  return <div className="consumption-preview-panel">
    <h2 className="inventory-section-title">Descuento por ventas</h2>
    <p className="consumption-preview-intro">Descuenta del inventario los insumos de lo que se vendió. Primero calcula la vista previa; nada se descuenta hasta que confirmes.</p>
    {error && <div className="alert alert--error">{error}</div>}

    <AutoPostingStatus />

    <form className="consumption-preview-filters" onSubmit={(event) => void calculate(event)}>
      <DateRangeField allowClear={false} from={filters.dateFrom} onChange={(dateFrom, dateTo) => setFilters({ ...filters, dateFrom, dateTo })} to={filters.dateTo} />
      <label className="field"><span>Tienda</span><select onChange={(event) => setFilters({ ...filters, storeId: event.target.value })} value={filters.storeId}>{getStoreScopeOptions(user?.role, stores).allowsAllStores && <option value="">Todas las tiendas</option>}{stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
      <Field label="Buscar producto" placeholder="Nombre, variante o SKU" value={filters.productSearch || ''} onChange={(productSearch) => setFilters({ ...filters, productSearch })} />
      <label className="coverage-check"><input checked={filters.onlyIssues} onChange={(event) => setFilters({ ...filters, onlyIssues: event.target.checked })} type="checkbox" />Mostrar solo pendientes</label>
      <label className="coverage-check"><input checked={filters.includeReceiptDetails} onChange={(event) => setFilters({ ...filters, includeReceiptDetails: event.target.checked })} type="checkbox" />Incluir detalle por ticket</label>
      <button className="button button--primary" disabled={isLoading || !filters.dateFrom || !filters.dateTo} type="submit">{isLoading ? 'Calculando...' : 'Calcular vista previa'}</button>
    </form>

    {!preview && !isLoading && <div className="empty-panel"><strong>Elige el rango y calcula la vista previa</strong><span>Ver la vista previa no descuenta nada; el descuento se confirma más abajo.</span></div>}
    {isLoading && <div className="inline-loading"><span className="spinner" />Calculando…</div>}

    {preview && !isLoading && <>
      {preview.excludedBeforeBaseline && preview.excludedBeforeBaseline.count > 0 && <div className="alert alert--info">
        {preview.excludedBeforeBaseline.count} ticket(s) anteriores al inicio del inventario{preview.excludedBeforeBaseline.baselineAt ? ` (${new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: DEFAULT_STORE_TIMEZONE }).format(new Date(preview.excludedBeforeBaseline.baselineAt))})` : ''} quedaron excluidos de la vista previa y del descuento: el conteo físico inicial ya refleja su consumo.
      </div>}
      {readiness && <section className={readiness.readyToPost ? 'readiness-card readiness-card--ready' : 'readiness-card readiness-card--blocked'}>
        <div>
          <strong>{readiness.readyToPost ? (alreadyApplied ? 'Descuento aplicado en este rango' : 'Listo para descontar') : 'Puedes descontar, con algunas ventas fuera'}</strong>
          <span>{readiness.readyToPost
            ? (alreadyApplied ? 'Todas las ventas del rango ya tienen su inventario descontado.' : 'No se detectaron pendientes en este rango. Puedes confirmar el descuento abajo.')
            : 'Las ventas con pendiente se quedan fuera y el resto sí se descuenta. Cada pendiente de abajo trae su botón para arreglarlo aquí mismo.'}</span>
        </div>
        <small>{readiness.blockingIssuesCount} pendiente(s) por arreglar · {readiness.warningsCount} aviso(s){typeof pendingCount === 'number' ? ` · ${pendingCount} venta(s) por descontar` : ''}</small>
      </section>}

      <section className="consumption-summary" aria-label="Resumen de la vista previa">
        <Summary label="Tickets analizados" value={preview.summary.totalReceiptsAnalyzed} />
        <Summary label="Líneas de venta analizadas" value={preview.summary.totalSaleLinesAnalyzed} />
        <Summary label="Productos con receta" value={preview.summary.totalSaleLinesWithRecipe} />
        <Summary label="Productos sin receta" value={preview.summary.totalSaleLinesMissingRecipe} />
        <Summary label="Recetas incompletas" value={preview.summary.totalSaleLinesWithIncompleteRecipe} />
        <Summary label="Insumos afectados" value={preview.summary.totalIngredientsImpacted} />
        <Summary label="Problemas de inventario" value={preview.summary.stockIssuesCount} />
        <Summary label="Problemas de unidades" value={preview.summary.unitMismatchCount} />
        <Summary label="Costo estimado" value={formatOptionalMoney(preview.summary.totalPlannedConsumptionCost)} />
        <Summary label="Costo real promedio" value={formatOptionalMoney(preview.summary.totalRealAverageConsumptionCost)} />
        <Summary label="Diferencia" value={formatOptionalSignedMoney(preview.summary.plannedVsRealDifference)} />
        <Summary label="Estado" value={alreadyApplied ? 'Aplicado ✓' : preview.summary.readyToPostPreview ? 'Listo para descontar' : 'Con pendientes'} />
      </section>

      <section className="preview-section">
        <div className="preview-section__heading"><div><h3>Consumo estimado por insumo</h3><span>{preview.pagination.totalConsumptionItems} resultado(s)</span></div></div>
        {preview.consumptionItems.length ? <>
          {/* En el teléfono la tabla se desplaza de lado y las cabeceras quedan INVISIBLES; el mismo selector que usan Compras y Existencias. */}
          <label className="field table-sort-mobile"><span>Ordenar por</span>
            <select disabled={isPaging} onChange={(event) => { const [key, dir] = event.target.value.split(':') as [SalesConsumptionSortKey, 'asc' | 'desc']; sortBy({ key, dir }); }} value={`${currentSort.key}:${currentSort.dir}`}>
              {sortChoices(SORT_WORDING, SORT_CHOICES, currentSort).map((opcion) => <option key={opcion.value} value={opcion.value}>{opcion.label}</option>)}
            </select>
          </label>
          <div className={tableWrapClass}><table className="data-table consumption-table"><thead><tr>{th('store', 'Tienda')}{th('ingredient', 'Insumo')}{/* Las cantidades mezclan unidades (ml, L, kg): ordenarlas como números compararía peras con manzanas. */}<th>Consumo en receta</th><th>Equivalente en inventario</th><th>Inventario actual</th><th>Quedaría en</th>{th('plannedCost', 'Costo estimado')}{th('realCost', 'Costo real promedio')}{th('difference', 'Diferencia')}{th('stockStatus', 'Estado inventario')}{th('realCostStatus', 'Estado costo real')}</tr></thead><tbody>{preview.consumptionItems.map((item) => <tr key={`${item.storeId}-${item.ingredientId}-${item.unitId ?? item.recipeUnitSymbol}`}>
          <td>{item.storeName}</td>
          <td><strong>{item.ingredientName}</strong></td>
          <td>{formatQuantity(item.recipeQuantity)} {item.recipeUnitSymbol}</td>
          <td>{item.stockEquivalentQuantity !== null && item.stockUnitSymbol ? <>{formatQuantity(item.stockEquivalentQuantity)} {item.stockUnitSymbol}{item.conversionDetail && <small>{item.conversionDetail}</small>}</> : 'No convertible'}</td>
          <td>{formatOptionalQuantity(item.currentQuantityOnHand)}</td>
          <td>{formatOptionalQuantity(item.projectedQuantityAfterPreview)}</td>
          <td>{formatOptionalMoney(item.plannedTotalCost)}</td>
          <td>{formatOptionalMoney(item.realAverageTotalCost)}</td>
          <td>{formatOptionalSignedMoney(item.plannedVsRealDifference)}</td>
          <td><StatusPill status={item.stockStatus} label={stockStatusLabel(item.stockStatus)} /></td>
          <td><StatusPill status={item.realCostStatus} label={realCostStatusLabel(item.realCostStatus)} /></td>
        </tr>)}</tbody></table></div>
          {applied && <Paginador count={preview.consumptionItems.length} disabled={isPaging} offset={preview.pagination.offset} onPage={(offset) => void loadPage(applied.value, { ...applied.page, offset })} total={preview.pagination.totalConsumptionItems} />}
        </> : <div className="empty-panel empty-panel--small"><strong>No hay consumo calculable</strong><span>Revisa el rango o los pendientes de recetas encontrados.</span></div>}
      </section>

      {returnsNotice && <div className="alert alert--success">{returnsNotice}</div>}
      {returns && (returns.totals.linesCount > 0 || returns.totals.blockedCount > 0) && <section className="preview-section returns-section">
        <div className="preview-section__heading"><div>
          <h3>Por regresar al inventario</h3>
          <span>Producto que volvió a la tienda: devoluciones y ventas canceladas que ya habían descontado</span>
        </div>{returns.totals.linesCount > 0 && <button className="button button--primary" disabled={isReturning} onClick={() => setReturnsModalOpen(true)} type="button">Regresar al inventario</button>}</div>
        {returns.totals.linesCount > 0 && <div className="data-table-wrap"><table className="data-table"><thead><tr><th>Motivo</th><th>Ticket</th><th>Producto</th><th>Tienda</th><th>Insumo</th><th>Cantidad que regresa</th></tr></thead><tbody>{returns.lines.map((line: InventoryReturnLine, index: number) => <tr key={`${line.receiptId}-${line.ingredientId}-${index}`}>
          <td><span className={`consumption-status consumption-status--${line.kind === 'REFUND' ? 'in-shift' : 'pending'}`}>{line.kind === 'REFUND' ? 'Devolución' : 'Venta cancelada'}</span></td>
          <td>{line.receiptNumber || '—'}</td>
          <td><strong>{line.itemName}</strong>{line.kind === 'REFUND' && <small>{line.soldQuantity} devuelto(s)</small>}</td>
          <td>{line.storeName}</td>
          <td>{line.ingredientName}</td>
          <td><strong>+{line.quantity} {line.unitSymbol}</strong></td>
        </tr>)}</tbody></table></div>}
        {returns.blocked.length > 0 && <div className="alert alert--warning">
          <strong>No se puede regresar todavía:</strong>
          <ul>{returns.blocked.map((row: InventoryReturnsPreview['blocked'][number], index: number) => <li key={index}>{row.receiptNumber ? `Ticket ${row.receiptNumber} · ` : ''}{row.itemName} — {row.reason}</li>)}</ul>
        </div>}
      </section>}

      {/* Dos bloques, no una tabla con columna de severidad: lo que TE FRENA y lo que sólo te
          avisa son cosas distintas, y mezclarlas obligaba a leer la severidad de cada renglón para
          saber cuál era cuál. Lo informativo va plegado: existe, pero no compite por atención. */}
      {blockingIssues.length > 0 && <section className="preview-section issues-section issues-section--blocking">
        <div className="preview-section__heading"><div>
          <h3>Estas ventas se quedarían fuera</h3>
          <span>{blockingIssues.length} pendiente(s) — el resto de las ventas sí se descuenta</span>
        </div></div>
        <ul className="issue-cards">{blockingIssues.map((issue, index) => <li className="issue-card issue-card--blocking" key={`b-${issue.issueType}-${index}`}>
          <div className="issue-card__head">
            <strong>{issueTypeLabel(issue.issueType)}</strong>
            <span className="issue-card__where">{issue.productName || issue.ingredientName || 'Varios productos'}{issue.storeName ? ` · ${issue.storeName}` : ''}</span>
          </div>
          <p className="issue-card__msg">{issue.message}</p>
          <div className="issue-card__fix">{fixAction(issue)}</div>
        </li>)}</ul>
      </section>}

      {infoIssues.length > 0 && <section className="preview-section issues-section">
        <details>
          <summary className="issues-summary">
            <strong>Solo para que lo sepas</strong>
            <span>{infoIssues.length} aviso(s) — no impiden descontar</span>
          </summary>
          <ul className="issue-cards">{infoIssues.map((issue, index) => <li className="issue-card" key={`i-${issue.issueType}-${index}`}>
            <div className="issue-card__head">
              <strong>{issueTypeLabel(issue.issueType)}</strong>
              <span className="issue-card__where">{issue.productName || issue.ingredientName || 'Varios productos'}{issue.storeName ? ` · ${issue.storeName}` : ''}</span>
            </div>
            <p className="issue-card__msg">{issue.message}</p>
            <div className="issue-card__fix">{fixAction(issue)}</div>
          </li>)}</ul>
        </details>
      </section>}

      {preview.issueItems.length === 0 && <section className="preview-section">
        <div className="empty-panel empty-panel--small"><strong>Sin pendientes</strong><span>Las recetas y el inventario cubren el rango seleccionado.</span></div>
      </section>}

      {filters.includeReceiptDetails && <section className="preview-section">
        <div className="preview-section__heading"><div><h3>Detalle por ticket</h3><span>{preview.pagination.totalReceiptItems} línea(s) de venta</span></div></div>
        {preview.receiptItems?.length ? <><div className={tableWrapClass}><table className="data-table consumption-receipts-table"><thead><tr><th>Ticket</th><th>Fecha</th><th>Tienda</th><th>Producto</th><th>Cantidad vendida</th><th>Receta</th><th>Estado</th><th>Insumos calculados</th><th>Pendientes</th></tr></thead><tbody>{preview.receiptItems.map((item) => <tr key={`${item.receiptId}-${item.productName}-${item.variantName || ''}`}>
          <td>{item.receiptNumber || 'Sin número'}</td>
          <td>{formatDate(item.receiptDate, resolveStoreTimezone(stores, { storeId: item.storeId }))}</td>
          <td>{item.storeName}</td>
          <td><strong>{item.productName}</strong>{item.variantName && <small>{item.variantName}</small>}</td>
          <td>{formatQuantity(item.quantitySold)}</td>
          <td>{item.recipeName || 'Sin receta'}</td>
          <td><StatusPill status={item.status} label={receiptStatusLabel(item.status)} /></td>
          <td>{item.ingredients.length ? <ul className="preview-ingredient-list">{item.ingredients.map((ingredient) => <li key={`${ingredient.ingredientId}`}>{ingredient.ingredientName}: {formatQuantity(ingredient.recipeQuantity)} {ingredient.recipeUnitSymbol}{ingredient.stockEquivalentQuantity !== null && ingredient.stockUnitSymbol ? ` = ${formatQuantity(ingredient.stockEquivalentQuantity)} ${ingredient.stockUnitSymbol}` : ''}{ingredient.conversionDetail ? ` (${ingredient.conversionDetail})` : ''} · Estimado {formatOptionalMoney(ingredient.plannedTotalCost)} · Real {formatOptionalMoney(ingredient.realAverageTotalCost)} · <StatusPill status={ingredient.stockStatus} label={stockStatusLabel(ingredient.stockStatus)} /></li>)}</ul> : 'No calculable'}</td>
          <td>{item.issues.length ? <ul className="preview-ingredient-list">{item.issues.map((issue, index) => <li key={`${issue.issueType}-${index}`}>{issue.message}</li>)}</ul> : '—'}</td>
        </tr>)}</tbody></table></div>
          {applied && <Paginador count={(preview.receiptItems ?? []).length} disabled={isPaging} offset={preview.pagination.receiptOffset ?? preview.pagination.offset} onPage={(receiptOffset) => void loadPage(applied.value, { ...applied.page, receiptOffset })} total={preview.pagination.totalReceiptItems} />}
        </> : <div className="empty-panel empty-panel--small"><strong>Sin detalle disponible</strong><span>No hay líneas de venta para mostrar.</span></div>}
      </section>}

      <section className="preview-section posting-section">
        <div className="preview-section__heading"><div><h3>Descuento real de inventario</h3><span>Al confirmar, el inventario baja según lo vendido. Hazlo después de revisar la vista previa.</span></div></div>
        {postingResult && <PostingResultSummary result={postingResult} />}
        {readiness && (
          alreadyApplied
            ? <button className="button button--secondary posting-applied" disabled type="button">Descuento aplicado ✓</button>
            : totalEligible === 0
              ? <p className="posting-blocked-note">No hay ventas en este rango.</p>
              // Antes, con un solo pendiente el botón NO existía y no quedaba nada que hacer más que
              // arreglarlo todo primero. Ahora siempre se puede descontar lo que sí se puede: lo que
              // quede fuera se reporta al terminar, y reintentar no repite lo ya descontado.
              : <button className="button button--danger" onClick={() => setIsPostingModalOpen(true)} type="button">
                {readiness.readyToPost ? 'Descontar inventario de este rango' : `Descontar lo que se pueda (${readiness.blockingIssuesCount} pendiente(s) sin resolver)`}
              </button>
        )}
        {alreadyApplied && <p className="posting-blocked-note">Las {postedCount} venta(s) del rango ya tienen su inventario descontado. Si entran ventas nuevas, este botón se activa solo al recalcular.</p>}
      </section>
    </>}

    <SalesPostingHistoryPanel stores={stores} />

    {isPostingModalOpen && preview && <ConfirmPostingModal
      preview={preview}
      filters={filters}
      storeName={storeName}
      onClose={() => setIsPostingModalOpen(false)}
      onPosted={(result) => {
        setPostingResult(result);
        setIsPostingModalOpen(false);
        // El estado del botón sale del backend: recalcular refresca pendientes y lo deja en "aplicado ✓".
        void runCalculation(filters, true);
      }}
    />}

    {fixModal?.kind === 'presentation' && <PresentationEditor
      ingredients={ingredients}
      units={units}
      value={fixModal.presentation}
      initialIngredientId={fixModal.ingredientId}
      defaultCostingDefault
      onClose={() => setFixModal(null)}
      onSaved={afterFixSaved}
    />}
    {fixModal?.kind === 'adjust' && <ManualMovementModal
      ingredients={ingredients}
      stores={stores}
      units={units}
      initialIngredientId={fixModal.ingredientId}
      initialStoreId={fixModal.storeId ?? undefined}
      focusCost={fixModal.focusCost}
      onClose={() => setFixModal(null)}
      onSaved={async () => { await afterFixSaved(); }}
      onIngredientCreated={(ingredient) => setIngredients((current) => [...current, ingredient])}
    />}

    {returnsModalOpen && returns && <ReturnsConfirmModal
      totals={returns.totals}
      isBusy={isReturning}
      onClose={() => setReturnsModalOpen(false)}
      onConfirm={(motivo) => void applyReturns(motivo)}
    />}
  </div>;
}

/**
 * Confirmación de los regresos al inventario. Antes era `window.prompt`: el cuadro gris del
 * navegador, con su "The page at … says", sin poder explicar qué se va a mover ni validar el motivo
 * antes de enviar. Un movimiento de inventario merece una ventana de la app, como todo lo demás.
 */
function ReturnsConfirmModal({ totals, isBusy, onClose, onConfirm }: {
  totals: InventoryReturnsPreview['totals'];
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState('Producto devuelto por el cliente');
  const valido = motivo.trim().length >= 10;
  return <Modal
    title="Regresar producto al inventario"
    description={`${totals.linesCount} movimiento(s) van a sumarse a tus existencias`}
    isBusy={isBusy}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isBusy} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--primary" disabled={isBusy || !valido} onClick={() => onConfirm(motivo)} type="button">
        {isBusy ? 'Regresando…' : 'Regresar al inventario'}
      </button>
    </>}
  >
    <dl className="detail-list">
      {totals.refundLines > 0 && <div><dt>Por devolución</dt><dd>{totals.refundLines} movimiento(s)</dd></div>}
      {totals.cancelledLines > 0 && <div><dt>Por venta cancelada</dt><dd>{totals.cancelledLines} movimiento(s)</dd></div>}
      {totals.alreadyApplied > 0 && <div><dt>Ya regresados antes</dt><dd>{totals.alreadyApplied} — no se vuelven a mover</dd></div>}
    </dl>
    <div className="alert alert--info">El inventario va a SUBIR con estas cantidades. Si el producto no volvió físicamente a la tienda, cancela.</div>
    <label className="field">
      <span>¿Por qué? (mínimo 10 caracteres)</span>
      <textarea onChange={(event) => setMotivo(event.target.value)} rows={3} value={motivo} />
    </label>
  </Modal>;
}

/**
 * Teclear DESCONTAR a mano es fricción a propósito, y es correcta cuando hay algo anómalo. Para una
 * corrida limpia sólo entrena a odiar la tarea: la vista previa ya mostró exactamente lo que va a
 * pasar. Se pide la palabra cuando hay ventas que se van a quedar FUERA — no por un aviso
 * informativo como "te va a quedar poco" o "no sabemos a cómo lo compras", que no cambian nada de
 * lo que está por ocurrir.
 */
function ConfirmPostingModal({ preview, filters, storeName, onClose, onPosted }: {
  preview: SalesConsumptionPreview;
  filters: SalesConsumptionPreviewFilters;
  storeName: string | null;
  onClose: () => void;
  onPosted: (result: SalesConsumptionPostResult) => void;
}) {
  const avisos = preview.issueItems.filter((issue) => issue.blocking).length;
  const pideConfirmacionEscrita = avisos > 0;
  const [confirmText, setConfirmText] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState<string>();

  const puedeConfirmar = !pideConfirmacionEscrita || confirmText === CONFIRM_WORD;

  const confirmPosting = async () => {
    if (!puedeConfirmar || isPosting) return;
    setIsPosting(true);
    setError(undefined);
    try {
      const result = await postSalesConsumption({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        storeId: filters.storeId || undefined,
        confirm: true,
      });
      onPosted(result);
    } catch (postError) {
      setError(getErrorMessage(postError, 'No fue posible descontar el inventario de este rango.'));
    } finally {
      setIsPosting(false);
    }
  };

  return <Modal
    title="Confirmar descuento de inventario"
    isBusy={isPosting}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isPosting} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--danger" disabled={isPosting || !puedeConfirmar} onClick={() => void confirmPosting()} type="button">{isPosting ? 'Descontando...' : 'Confirmar descuento'}</button>
    </>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    <dl className="detail-list">
      <div><dt>Rango de fechas</dt><dd>{formatDateOnly(filters.dateFrom)} – {formatDateOnly(filters.dateTo)}</dd></div>
      <div><dt>Tienda</dt><dd>{storeName ?? 'Todas las tiendas'}</dd></div>
      <div><dt>Tickets analizados</dt><dd>{preview.summary.totalReceiptsAnalyzed}</dd></div>
      <div><dt>Líneas de venta</dt><dd>{preview.summary.totalSaleLinesAnalyzed}</dd></div>
      <div><dt>Insumos afectados</dt><dd>{preview.summary.totalIngredientsImpacted}</dd></div>
      <div><dt>Costo real total estimado</dt><dd>{formatOptionalMoney(preview.summary.totalRealAverageConsumptionCost)}</dd></div>
    </dl>
    <div className="alert alert--warning">Esta acción descuenta del inventario los insumos de las ventas del rango que se puedan descontar. No se puede deshacer con un clic.</div>
    {pideConfirmacionEscrita && <label className="field">
      <span>{avisos} venta(s) van a quedar fuera por un pendiente sin resolver. Escribe {CONFIRM_WORD} para descontar el resto de todos modos</span>
      <input onChange={(event) => setConfirmText(event.target.value)} placeholder={CONFIRM_WORD} value={confirmText} />
    </label>}
  </Modal>;
}

function PostingResultSummary({ result }: { result: SalesConsumptionPostResult }) {
  return <div className="posting-result">
    <div className={result.totalTransactionsCreated > 0 ? 'alert alert--success' : 'alert alert--warning'}>{result.message}</div>
    <div className="consumption-summary" aria-label="Resultado del descuento">
      <Summary label="Ventas descontadas" value={result.linesPostedCount} />
      <Summary label="Insumos descontados" value={result.totalIngredientsDeducted} />
      <Summary label="Costo real total" value={formatOptionalMoney(result.totalRealCostDeducted)} />
      <Summary label="Ya estaban descontadas" value={result.skippedAlreadyPostedCount} />
      <Summary label="Quedaron pendientes" value={result.linesSkippedCount} />
    </div>
    {result.skippedLines.length > 0 && <section className="preview-section issues-section issues-section--blocking">
      <div className="preview-section__heading"><div>
        <h3>Estas ventas quedaron pendientes</h3>
        <span>Arregla el motivo y vuelve a descontar: lo ya descontado no se repite.</span>
      </div></div>
      <ul className="issue-cards">{result.skippedLines.map((skipped, index) => <li className="issue-card issue-card--blocking" key={`s-${skipped.reason}-${index}`}>
        <div className="issue-card__head">
          <strong>{issueTypeLabel(skipped.reason)}</strong>
          <span className="issue-card__where">{skipped.productName}{skipped.storeName ? ` · ${skipped.storeName}` : ''}{skipped.receiptNumber ? ` · ticket ${skipped.receiptNumber}` : ''}</span>
        </div>
        <p className="issue-card__msg">{skipped.message}</p>
      </li>)}</ul>
    </section>}
    {result.balanceUpdates.length > 0 && <div className="data-table-wrap"><table className="data-table consumption-table"><thead><tr><th>Tienda</th><th>Insumo</th><th>Cantidad descontada</th><th>Antes</th><th>Después</th><th>Costo real promedio</th><th>Costo real</th></tr></thead><tbody>{result.balanceUpdates.map((update, index) => <tr key={`${update.storeName}-${update.ingredientName}-${index}`}>
      <td>{update.storeName}</td>
      <td>{update.ingredientName}</td>
      <td>{formatQuantity(update.quantityDeducted)} {update.unitSymbol}</td>
      <td>{formatQuantity(update.balanceBefore)}</td>
      <td>{formatQuantity(update.balanceAfter)}</td>
      <td>{formatOptionalMoney(update.realAverageUnitCost)}</td>
      <td>{formatOptionalMoney(update.realTotalCost)}</td>
    </tr>)}</tbody></table></div>}
  </div>;
}

function Field({ label, value, onChange, type = 'text', placeholder }: { label: string; value: string; onChange: (value: string) => void; type?: string; placeholder?: string }) {
  return <label className="field"><span>{label}</span><input onChange={(event) => onChange(event.target.value)} placeholder={placeholder} type={type} value={value} /></label>;
}
/** Anterior/Siguiente de una tabla paginada en el servidor. El mismo lenguaje que Compras y Gastos. */
function Paginador({ offset, count, total, disabled, onPage }: { offset: number; count: number; total: number; disabled: boolean; onPage: (offset: number) => void }) {
  return <div className="coverage-pagination"><span>Mostrando {offset + 1}–{offset + count} de {total}</span><div>
    <button className="button button--ghost" disabled={disabled || offset === 0} onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))} type="button">Anterior</button>
    <button className="button button--ghost" disabled={disabled || offset + count >= total} onClick={() => onPage(offset + PAGE_SIZE)} type="button">Siguiente</button>
  </div></div>;
}

/**
 * Lo que viaja al servidor. El orden y el desplazamiento del detalle se OMITEN cuando valen lo de
 * por defecto: el esquema es strict y un backend anterior los rechazaría con 400 durante la
 * ventana de despliegue. offset y limit ya existían y viajan siempre.
 */
function buildRequest(value: SalesConsumptionPreviewFilters, page: PageState): SalesConsumptionPreviewFilters {
  const sort = value.sort ?? DEFAULT_SORT.key;
  const dir = value.dir ?? DEFAULT_SORT.dir;
  const ordenPorDefecto = sort === DEFAULT_SORT.key && dir === DEFAULT_SORT.dir;
  return {
    dateFrom: value.dateFrom,
    dateTo: value.dateTo,
    storeId: value.storeId || undefined,
    productSearch: value.productSearch?.trim() || undefined,
    onlyIssues: value.onlyIssues,
    includeReceiptDetails: value.includeReceiptDetails,
    ...(ordenPorDefecto ? {} : { sort, dir }),
    limit: PAGE_SIZE,
    offset: page.offset,
    ...(page.receiptOffset > 0 ? { receiptOffset: page.receiptOffset } : {}),
  };
}

function Summary({ label, value }: { label: string; value: string | number }) { return <article><span>{label}</span><strong>{value}</strong></article>; }
function StatusPill({ status, label }: { status: string; label: string }) { return <span className={`consumption-status consumption-status--${status.toLowerCase()}`}>{label}</span>; }
// La severidad dejó de pintarse: ahora la distinción que importa ("me frena" vs "sólo te aviso") la
// hace la SECCIÓN en la que cae el pendiente, no una etiqueta que había que leer renglón por renglón.

function stockStatusLabel(status: StockPreviewStatus) {
  return {
    OK: 'Suficiente',
    LOW_AFTER_CONSUMPTION: 'Quedaría bajo',
    INSUFFICIENT_STOCK: 'No alcanza',
    NO_STOCK_RECORD: 'Sin registro',
    UNIT_MISMATCH: 'Unidad incompatible',
    INCOMPLETE_RECIPE: 'Receta incompleta',
    MISSING_RECIPE: 'Sin receta',
    UNKNOWN_STORE_MAPPING: 'Tienda sin relacionar',
  }[status];
}
function realCostStatusLabel(status: RealCostStatus) {
  return {
    REAL_COST_AVAILABLE: 'Costo real disponible',
    MISSING_REAL_COST: 'Sin costo real',
    UNIT_MISMATCH: 'Unidad incompatible',
    NO_STORE_SELECTED: 'Sin tienda',
    NO_STOCK_RECORD: 'Sin registro',
  }[status];
}
/**
 * El nombre del problema debe ser la instrucción para resolverlo. Los nombres anteriores («Sin costo
 * real», «Costeo de respaldo») describían el estado interno del sistema: correctos, pero había que
 * traducirlos mentalmente antes de saber qué hacer.
 */
function issueTypeLabel(value: ConsumptionIssueType) {
  return {
    MISSING_RECIPE: 'Falta la receta',
    INCOMPLETE_RECIPE: 'La receta está a medias',
    MISSING_RECIPE_COMPONENT: 'La receta no tiene insumos',
    MISSING_INGREDIENT: 'El insumo ya no existe',
    MISSING_UNIT: 'La unidad ya no existe',
    NO_STOCK_RECORD: 'Este insumo no está en el inventario',
    INSUFFICIENT_STOCK: 'No alcanza el inventario',
    UNIT_MISMATCH: 'La receta y el inventario usan unidades distintas',
    MISSING_REAL_COST: 'Nunca has comprado este insumo',
    UNKNOWN_STORE_MAPPING: 'La venta no está ligada a una tienda',
    CANCELLED_OR_REFUNDED_IGNORED: 'Cancelada o devuelta: no se descuenta',
    LOW_AFTER_CONSUMPTION: 'Te va a quedar poco',
    COST_DIFFERENCE_HIGH: 'El precio no cuadra con tu última compra',
    MISSING_PURCHASE_PRICE: 'No sabemos a cómo lo compras',
    FALLBACK_COSTING_PRESENTATION: 'Se usó otro precio para estimar',
    HISTORICAL_MOVEMENT_WITHOUT_BALANCE: 'Movimiento viejo sin saldo',
  }[value];
}
function receiptStatusLabel(value: 'OK' | 'MISSING_RECIPE' | 'INCOMPLETE_RECIPE' | 'UNKNOWN_STORE_MAPPING') {
  return { OK: 'Calculable', MISSING_RECIPE: 'Sin receta', INCOMPLETE_RECIPE: 'Receta incompleta', UNKNOWN_STORE_MAPPING: 'Tienda sin relacionar' }[value];
}
function formatOptionalQuantity(value: string | null) { return value === null ? 'Sin registro' : formatQuantity(value); }
function formatDate(value: string | null, timeZone: string = DEFAULT_STORE_TIMEZONE) { return value ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value)) : 'Sin fecha'; }
function formatDateOnly(value: string) { return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00.000Z`)); }
function toDateInput(value: Date) { return dateToStoreISO(value); }
